/**
 * 埋点事件追踪器
 * 
 * @description 负责收集和发送队列系统的用户行为数据
 * @author Frontend Architect
 * @version 2.0.0
 */

import type { TrackingEventData, QueueTask } from '@/types/taskQueue';
import { useEventTracking } from '@/hooks/useEventTracking';

/** 埋点事件追踪器接口 */
export interface IEventTracker {
  /** 追踪队列进入事件 */
  trackQueueEnter(task: QueueTask): void;
  
  /** 追踪阶段变更事件 */
  trackStageChange(task: QueueTask, previousStage: string): void;
  
  /** 追踪排队减少事件 */
  trackQueueReduction(task: QueueTask, reductionData: QueueReductionData): void;
  
  /** 追踪生成完成事件 */
  trackGenerationComplete(task: QueueTask, completionData: GenerationCompletionData): void;
  
  /** 追踪用户退出事件 */
  trackUserExit(task: QueueTask, exitReason: string): void;
  
  /** 发送自定义事件 */
  trackCustomEvent(eventData: TrackingEventData): void;
}

/** 排队减少数据接口 */
export interface QueueReductionData {
  /** 减少前排队数 */
  beforeQueueNum: number;
  /** 减少后排队数 */
  afterQueueNum: number;
  /** 减少数量 */
  reductionAmount: number;
  /** 减少百分比 */
  reductionPercentage: number;
}

/** 生成完成数据接口 */
export interface GenerationCompletionData {
  /** 是否成功 */
  success: boolean;
  /** 总等待时间（秒） */
  totalWaitTime: number;
  /** 实际生成时间（秒） */
  actualGenerationTime: number;
  /** 错误信息（如果失败） */
  errorMessage?: string;
}

/** 性能指标数据接口 */
export interface PerformanceMetrics {
  /** 各阶段平均等待时间 */
  averageWaitTimeByStage: Record<string, number>;
  /** 用户流失率 */
  churnRate: number;
  /** 不同会员等级的服务质量对比 */
  serviceQualityByUserType: Record<string, {
    averageWaitTime: number;
    completionRate: number;
    userSatisfaction: number;
  }>;
}

/** 默认埋点事件追踪器实现 */
export class DefaultEventTracker implements IEventTracker {
  private isEnabled: boolean;
  private eventQueue: TrackingEventData[] = [];
  private flushTimer: NodeJS.Timeout | null = null;
  private readonly FLUSH_INTERVAL = 5000; // 5秒批量发送一次
  private readonly MAX_QUEUE_SIZE = 100; // 最大队列长度

  constructor(enabled = true) {
    this.isEnabled = enabled;
    
    if (this.isEnabled) {
      this.startFlushTimer();
    }
  }

  trackQueueEnter(task: QueueTask): void {
    if (!this.isEnabled) return;

    const eventData: TrackingEventData = {
      eventType: 'queue_enter',
      clientId: task.clientId,
      userType: task.userType,
      module: task.module,
      timestamp: Date.now(),
      data: {
        initialQueueNum: task.queueNum,
        initialQueueTotalNum: task.queueTotalNum,
        estimatedWaitTime: task.estimatedWaitTime,
        useNewQueue: task.useNewQueue
      }
    };

    this.enqueueEvent(eventData);
  }

  trackStageChange(task: QueueTask, previousStage: string): void {
    if (!this.isEnabled) return;

    const stageStartTime = task.stageStartTime;
    const stageDuration = stageStartTime ? Date.now() - stageStartTime : 0;

    const eventData: TrackingEventData = {
      eventType: 'stage_change',
      clientId: task.clientId,
      userType: task.userType,
      module: task.module,
      timestamp: Date.now(),
      data: {
        previousStage,
        currentStage: task.stage,
        stageDuration,
        totalWaitTime: task.totalWaitTime,
        progress: task.progress
      }
    };

    this.enqueueEvent(eventData);
  }

  trackQueueReduction(task: QueueTask, reductionData: QueueReductionData): void {
    if (!this.isEnabled) return;

    const eventData: TrackingEventData = {
      eventType: 'queue_reduction',
      clientId: task.clientId,
      userType: task.userType,
      module: task.module,
      timestamp: Date.now(),
      data: {
        ...reductionData,
        currentEstimatedWaitTime: task.estimatedWaitTime
      }
    };

    this.enqueueEvent(eventData);
  }

  trackGenerationComplete(task: QueueTask, completionData: GenerationCompletionData): void {
    if (!this.isEnabled) return;

    const eventData: TrackingEventData = {
      eventType: 'generation_complete',
      clientId: task.clientId,
      userType: task.userType,
      module: task.module,
      timestamp: Date.now(),
      data: {
        ...completionData,
        finalProgress: task.progress,
        totalTaskTime: Date.now() - task.createdAt
      }
    };

    this.enqueueEvent(eventData);

    // 重要事件立即发送
    this.flushEvents();
  }

  trackUserExit(task: QueueTask, exitReason: string): void {
    if (!this.isEnabled) return;

    const eventData: TrackingEventData = {
      eventType: 'user_exit',
      clientId: task.clientId,
      userType: task.userType,
      module: task.module,
      timestamp: Date.now(),
      data: {
        exitReason,
        exitStage: task.stage,
        timeInQueue: Date.now() - task.createdAt,
        progress: task.progress,
        queueNumAtExit: task.queueNum
      }
    };

    this.enqueueEvent(eventData);

    // 用户退出事件立即发送
    this.flushEvents();
  }

  trackCustomEvent(eventData: TrackingEventData): void {
    if (!this.isEnabled) return;
    this.enqueueEvent(eventData);
  }

  /** 将事件加入队列 */
  private enqueueEvent(eventData: TrackingEventData): void {
    this.eventQueue.push(eventData);

    // 如果队列满了，立即发送
    if (this.eventQueue.length >= this.MAX_QUEUE_SIZE) {
      this.flushEvents();
    }
  }

  /** 批量发送事件 */
  private flushEvents(): void {
    if (this.eventQueue.length === 0) return;

    const events = [...this.eventQueue];
    this.eventQueue = [];

    // 使用现有的埋点系统发送数据
    this.sendEvents(events);
  }

  /** 发送事件数据 */
  private sendEvents(events: TrackingEventData[]): void {
    try {
      // 为每个事件单独发送埋点
      events.forEach(event => {
        useEventTracking({
          event: 'queue_system_event',
          eventValue: {
            event_type: event.eventType,
            client_id: event.clientId,
            user_type: event.userType,
            module: event.module,
            timestamp: event.timestamp,
            data: JSON.stringify(event.data)
          }
        });
      });

      // 可以在这里添加额外的发送逻辑，比如发送到专用的队列分析服务
    } catch (error) {
      // 发送失败时处理
      
      // 发送失败时，可以考虑重新加入队列或写入本地存储
      if (events.length <= 10) { // 避免无限重试
        this.eventQueue.unshift(...events);
      }
    }
  }

  /** 启动批量发送定时器 */
  private startFlushTimer(): void {
    this.flushTimer = setInterval(() => {
      this.flushEvents();
    }, this.FLUSH_INTERVAL);
  }

  /** 停止并清理追踪器 */
  destroy(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }

    // 发送剩余事件
    this.flushEvents();
  }

  /** 启用追踪 */
  enable(): void {
    if (!this.isEnabled) {
      this.isEnabled = true;
      this.startFlushTimer();
    }
  }

  /** 禁用追踪 */
  disable(): void {
    if (this.isEnabled) {
      this.isEnabled = false;
      this.destroy();
    }
  }
}

/** 空操作追踪器（用于禁用追踪的场景） */
export class NoOpEventTracker implements IEventTracker {
  trackQueueEnter(): void {}
  trackStageChange(): void {}
  trackQueueReduction(): void {}
  trackGenerationComplete(): void {}
  trackUserExit(): void {}
  trackCustomEvent(): void {}
}

/** 追踪器工厂 */
export class EventTrackerFactory {
  /** 创建追踪器实例 */
  static createTracker(enabled: boolean): IEventTracker {
    return enabled ? new DefaultEventTracker(enabled) : new NoOpEventTracker();
  }
} 